const DEFAULT_HEARTBEAT_INTERVAL_MS = 20000;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 10000;
const DEFAULT_HEARTBEAT_FAILURE_THRESHOLD = 3;
const DEFAULT_HEARTBEAT_WARNING_POSITION = 'bottom-right';
const HEARTBEAT_TOOLTIP_TEXT = 'Connection with the server failed.';
const HEARTBEAT_URL_SELECTOR = '.tickets-heartbeat-url, input[name="tickets_heartbeat_url"]';
const HEARTBEAT_OVERLAY_ID = 'tickets-heartbeat-warning-overlay';
const HEARTBEAT_TOOLTIP_ID = 'tickets-heartbeat-warning-tooltip';

let heartbeatIntervalId = null;
let warningTooltip = null;
let warningPopperInstance = null;

/**
 * @param {number} value
 * @param {number} fallback
 * @return {number}
 */
function normalizePositiveNumber(value, fallback) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return fallback;
    }

    return numericValue;
}

/**
 * @return {{intervalMs: number, timeoutMs: number, failureThreshold: number, warningPosition: string}}
 */
function getTicketsHeartbeatConfig() {
    return {
        intervalMs: normalizePositiveNumber(
            window.__TICKETS_HEARTBEAT_INTERVAL_MS,
            DEFAULT_HEARTBEAT_INTERVAL_MS
        ),
        timeoutMs: normalizePositiveNumber(
            window.__TICKETS_HEARTBEAT_TIMEOUT_MS,
            DEFAULT_HEARTBEAT_TIMEOUT_MS
        ),
        failureThreshold: normalizePositiveNumber(
            window.__TICKETS_HEARTBEAT_FAILURE_THRESHOLD,
            DEFAULT_HEARTBEAT_FAILURE_THRESHOLD
        ),
        warningPosition: window.__TICKETS_HEARTBEAT_WARNING_POSITION || DEFAULT_HEARTBEAT_WARNING_POSITION,
    };
}

/**
 * @return {string|null}
 */
function getTicketsHeartbeatUrl() {
    const input = document.querySelector(HEARTBEAT_URL_SELECTOR);

    if (!(input instanceof HTMLInputElement) || !input.value) {
        return null;
    }

    return input.value;
}

/**
 * @return {*|null}
 */
function getAxiosClient() {
    if (window.axios) {
        return window.axios;
    }

    // noinspection JSUnresolvedReference
    if (typeof axios !== 'undefined') {
        // noinspection JSUnresolvedReference
        return axios;
    }

    return null;
}

function removeWarningTooltip() {
    if (warningPopperInstance) {
        warningPopperInstance.destroy();
        warningPopperInstance = null;
    }

    if (warningTooltip && warningTooltip.parentElement) {
        warningTooltip.parentElement.removeChild(warningTooltip);
    }

    warningTooltip = null;
}

function removeWarningOverlay() {
    removeWarningTooltip();

    const existingOverlay = document.getElementById(HEARTBEAT_OVERLAY_ID);

    if (existingOverlay && existingOverlay.parentElement) {
        existingOverlay.parentElement.removeChild(existingOverlay);
    }
}

/**
 * @param {HTMLElement} icon
 * @param {HTMLElement} tooltip
 */
function positionWarningTooltipFallback(icon, tooltip) {
    const margin = 12;
    const offset = 10;
    const iconRect = icon.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    tooltip.style.position = 'fixed';

    const centeredLeft = iconRect.left + (iconRect.width / 2) - (tooltipRect.width / 2);
    const maxLeft = window.innerWidth - tooltipRect.width - margin;
    const left = Math.max(margin, Math.min(centeredLeft, maxLeft));

    let top = iconRect.top - tooltipRect.height - offset;
    if (top < margin) {
        top = iconRect.bottom + offset;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
}

/**
 * @param {HTMLElement} icon
 */
function showWarningTooltip(icon) {
    if (warningTooltip) {
        warningTooltip.style.display = 'block';

        if (warningPopperInstance) {
            void warningPopperInstance.update();
        } else {
            positionWarningTooltipFallback(icon, warningTooltip);
        }

        return;
    }

    warningTooltip = document.createElement('div');
    warningTooltip.id = HEARTBEAT_TOOLTIP_ID;
    warningTooltip.textContent = HEARTBEAT_TOOLTIP_TEXT;
    warningTooltip.style.background = '#343a40';
    warningTooltip.style.borderRadius = '4px';
    warningTooltip.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    warningTooltip.style.color = '#fff';
    warningTooltip.style.fontSize = '13px';
    warningTooltip.style.lineHeight = '1.3';
    warningTooltip.style.maxWidth = '260px';
    warningTooltip.style.padding = '8px 10px';
    warningTooltip.style.pointerEvents = 'none';
    warningTooltip.style.zIndex = '2147483647';

    document.body.appendChild(warningTooltip);

    if (window.Popper && typeof window.Popper.createPopper === 'function') {
        warningPopperInstance = window.Popper.createPopper(icon, warningTooltip, {
            placement: 'top',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 10],
                    },
                },
            ],
        });
        return;
    }

    positionWarningTooltipFallback(icon, warningTooltip);
}

function hideWarningTooltip() {
    if (!warningTooltip) {
        return;
    }

    warningTooltip.style.display = 'none';
}

/**
 * @param {string} warningPosition
 */
function showWarningOverlay(warningPosition) {
    if (document.getElementById(HEARTBEAT_OVERLAY_ID)) {
        return;
    }

    const overlay = document.createElement('div');
    overlay.id = HEARTBEAT_OVERLAY_ID;
    overlay.style.bottom = '0';
    overlay.style.left = '0';
    overlay.style.pointerEvents = 'none';
    overlay.style.position = 'fixed';
    overlay.style.right = '0';
    overlay.style.top = '0';
    overlay.style.zIndex = '2147483646';

    const icon = document.createElement('i');
    icon.className = 'fas fa-exclamation-triangle';
    icon.setAttribute('aria-label', HEARTBEAT_TOOLTIP_TEXT);
    icon.setAttribute('role', 'img');
    icon.tabIndex = 0;
    icon.style.background = '#fff3cd';
    icon.style.border = '1px solid #ffeeba';
    icon.style.borderRadius = '50%';
    icon.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    icon.style.color = '#856404';
    icon.style.cursor = 'help';
    icon.style.fontSize = '22px';
    icon.style.height = '44px';
    icon.style.lineHeight = '42px';
    icon.style.pointerEvents = 'auto';
    icon.style.position = 'absolute';
    icon.style.textAlign = 'center';
    icon.style.width = '44px';

    applyWarningIconPosition(icon, warningPosition);

    icon.addEventListener('mouseenter', () => showWarningTooltip(icon));
    icon.addEventListener('mouseleave', hideWarningTooltip);
    icon.addEventListener('focus', () => showWarningTooltip(icon));
    icon.addEventListener('blur', hideWarningTooltip);
    icon.addEventListener('click', () => showWarningTooltip(icon));

    overlay.appendChild(icon);
    document.body.appendChild(overlay);
}

/**
 * @param {HTMLElement} icon
 * @param {string} warningPosition
 */
function applyWarningIconPosition(icon, warningPosition) {
    const position = String(warningPosition || DEFAULT_HEARTBEAT_WARNING_POSITION).toLowerCase();
    const verticalPosition = position.indexOf('top') !== -1 ? 'top' : 'bottom';
    const horizontalPosition = position.indexOf('left') !== -1 ? 'left' : 'right';

    icon.style[verticalPosition] = '24px';
    icon.style[horizontalPosition] = '24px';
}

function reloadCurrentPage() {
    window.location.reload();
}

/**
 * @param {number|null} intervalId
 */
function stopHeartbeatInterval(intervalId) {
    if (intervalId !== null) {
        clearInterval(intervalId);
    }

    heartbeatIntervalId = null;
}

function showCsrfReloadPrompt() {
    if (!window.Swal || typeof window.Swal.fire !== 'function') {
        reloadCurrentPage();
        return;
    }

    let reloadStarted = false;
    const reloadOnce = () => {
        if (reloadStarted) {
            return;
        }

        reloadStarted = true;
        reloadCurrentPage();
    };

    const swalOptions = /** @type {*} */ ({
        title: 'Session expired',
        text: 'Please reload the page to continue.',
        icon: 'warning',
        confirmButtonText: 'Reload',
        allowOutsideClick: false,
        allowEscapeKey: true,
        willClose: reloadOnce,
    });

    window.Swal.fire(swalOptions).then(reloadOnce);
}

/**
 * @param {*} error
 * @return {boolean}
 */
function isCsrfError(error) {
    return Boolean(error && error.response && Number(error.response.status) === 419);
}

function initTicketsHeartbeat() {
    if (window._TICKETS_HEARTBEAT_INITIALIZED) {
        return;
    }

    const heartbeatUrl = getTicketsHeartbeatUrl();

    if (!heartbeatUrl) {
        return;
    }

    const axiosClient = getAxiosClient();

    if (!axiosClient || typeof axiosClient.post !== 'function') {
        return;
    }

    window._TICKETS_HEARTBEAT_INITIALIZED = true;

    const config = getTicketsHeartbeatConfig();
    let failureCount = 0;

    const runHeartbeat = () => {
        axiosClient.post(heartbeatUrl, {}, {
            timeout: config.timeoutMs,
        }).then(() => {
            failureCount = 0;
            removeWarningOverlay();
        }).catch((error) => {
            if (isCsrfError(error)) {
                stopHeartbeatInterval(heartbeatIntervalId);
                showCsrfReloadPrompt();
                return;
            }

            failureCount += 1;

            if (failureCount > config.failureThreshold) {
                showWarningOverlay(config.warningPosition);
            }
        });
    };

    heartbeatIntervalId = setInterval(runHeartbeat, config.intervalMs);
}

function reinitializeTicketsHeartbeat() {
    stopHeartbeatInterval(heartbeatIntervalId);
    removeWarningOverlay();
    window._TICKETS_HEARTBEAT_INITIALIZED = false;
    initTicketsHeartbeat();
}

if (typeof window !== 'undefined') {
    window.__TICKETS_HEARTBEAT_REINITIALIZE = reinitializeTicketsHeartbeat;
}

export {
    getTicketsHeartbeatConfig,
    getTicketsHeartbeatUrl,
    initTicketsHeartbeat,
    isCsrfError,
    reinitializeTicketsHeartbeat,
    removeWarningOverlay,
};
